import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Theme, Locale, ColumnDefaults } from '@/types'
import { INIT_DEFAULTS } from '@/utils/normalizers'
import { settingsStorage } from './persistedStorage'
import i18n from '@/i18n'

export { INIT_DEFAULTS }

// ── Types ─────────────────────────────────────────────────────────────────────

/** Line style for relationships. Legacy: S.defaultLineMode */
export type LineMode = 'curved' | 'orthogonal' | 'straight'

interface SettingsState {
  // ── Theme ──────────────────────────────────────────────────────────────────
  /**
   * User's explicit theme preference.
   * - 'system' → follow OS prefers-color-scheme (default when never chosen)
   * - 'light' / 'dark' → override OS preference
   *
   * Persisted. Legacy equivalent: S.theme / morpherd_theme_preference
   */
  themePreference: Theme

  /**
   * The actual applied theme after resolving 'system' against the OS setting.
   * Set by the useTheme hook (T-31) when it detects prefers-color-scheme.
   * NOT persisted — recalculated on each session start.
   *
   * Only 'light' | 'dark' (never 'system').
   */
  resolvedTheme: 'light' | 'dark'

  // ── Language / Locale ─────────────────────────────────────────────────────
  /**
   * Active locale. Persisted via settingsStore.
   * Legacy equivalent: S.metadata.locale
   */
  language: Locale

  // ── Canvas visual settings ─────────────────────────────────────────────────
  /** Show/hide the background dot-grid. Legacy: S.grid */
  showGrid: boolean

  /** Enable dashed-line animation on relationships. Legacy: S.relAnimation */
  relationshipAnimationEnabled: boolean

  /** Default line routing style for new relationships. Legacy: S.defaultLineMode */
  defaultLineMode: LineMode

  // ── Sidebar state ─────────────────────────────────────────────────────────
  /** Whether the left sidebar is hidden. */
  sidebarHidden: boolean
  /** Which sidebar group sections are collapsed. Keyed by group ID. */
  sbCollapsed: Record<string, boolean>

  // ── Column defaults ────────────────────────────────────────────────────────
  /**
   * Default prefix/suffix columns applied when creating new tables.
   * Legacy equivalent: S.defaults
   */
  defaults: ColumnDefaults

  // ── Theme actions ──────────────────────────────────────────────────────────

  /** Set an explicit theme preference. */
  setThemePreference: (theme: Theme) => void

  /**
   * Cycle through system → light → dark → system.
   * Called by the theme toggle button.
   */
  cycleThemePreference: () => void

  /**
   * Set the resolved (applied) theme.
   * Called by the useTheme hook (T-31) when OS preference changes.
   * NOT user-facing.
   */
  setResolvedTheme: (theme: 'light' | 'dark') => void

  // ── Language actions ───────────────────────────────────────────────────────

  setLanguage: (locale: Locale) => void

  // ── Grid actions ───────────────────────────────────────────────────────────

  toggleGrid: () => void
  setGrid: (value: boolean) => void

  // ── Relationship animation actions ─────────────────────────────────────────

  toggleRelationshipAnimation: () => void
  setRelationshipAnimation: (value: boolean) => void

  // ── Line mode ──────────────────────────────────────────────────────────────

  setDefaultLineMode: (mode: LineMode) => void

  // ── Sidebar ────────────────────────────────────────────────────────────────

  setSidebarHidden: (hidden: boolean) => void
  toggleSidebar: () => void
  toggleGroupCollapse: (groupId: string) => void

  // ── Defaults actions ───────────────────────────────────────────────────────

  /** Partial or full update of column defaults. */
  updateDefaults: (patch: Partial<ColumnDefaults>) => void

  /** Replace defaults entirely. */
  setDefaults: (defaults: ColumnDefaults) => void

  /** Reset defaults to the built-in initial values. */
  resetDefaults: () => void

  // ── Legacy aliases (kept for code still in migration) ─────────────────────
  /** @deprecated Use themePreference */
  theme: Theme
  /** @deprecated Use language */
  locale: Locale
  /** @deprecated Use showGrid */
  grid: boolean
  /** @deprecated Use relationshipAnimationEnabled */
  relAnimation: boolean
  /** @deprecated Use setThemePreference */
  setTheme: (theme: Theme) => void
  /** @deprecated Use cycleThemePreference */
  cycleTheme: () => void
  /** @deprecated Use setLanguage */
  setLocale: (locale: Locale) => void
  /** @deprecated Use toggleGrid */
  toggleRelAnimation: () => void
}

// ── Cycle order ───────────────────────────────────────────────────────────────
const THEME_CYCLE: Theme[] = ['system', 'light', 'dark']

// ── Store ─────────────────────────────────────────────────────────────────────
export const useSettingsStore = create<SettingsState>()(
  persist(
    (set, get) => ({
      // ── Initial state ──────────────────────────────────────────────────────
      themePreference:              'system',
      resolvedTheme:                'light',   // overwritten by useTheme hook at runtime
      language:                     'pt-BR',
      showGrid:                     true,
      relationshipAnimationEnabled: true,
      defaultLineMode:              'orthogonal',
      sidebarHidden:                false,
      sbCollapsed:                  {},
      defaults:                     INIT_DEFAULTS,

      // Legacy mirrors (kept in sync via every set call)
      theme:       'system',
      locale:      'pt-BR',
      grid:        true,
      relAnimation: true,

      // ── Theme ──────────────────────────────────────────────────────────────

      setThemePreference: (themePreference) =>
        set({ themePreference, theme: themePreference }),

      cycleThemePreference: () => {
        const { themePreference } = get()
        const idx  = THEME_CYCLE.indexOf(themePreference)
        const next = THEME_CYCLE[(idx + 1) % THEME_CYCLE.length]
        set({ themePreference: next, theme: next })
      },

      // resolvedTheme is NOT persisted — always recalculated by useTheme hook
      setResolvedTheme: (resolvedTheme) => set({ resolvedTheme }),

      // ── Language ───────────────────────────────────────────────────────────

      setLanguage: (language) => {
        void i18n.changeLanguage(language)
        set({ language, locale: language })
      },

      // ── Grid ───────────────────────────────────────────────────────────────

      toggleGrid: () => set((s) => ({ showGrid: !s.showGrid, grid: !s.showGrid })),

      setGrid: (value) => set({ showGrid: value, grid: value }),

      // ── Relationship animation ─────────────────────────────────────────────

      toggleRelationshipAnimation: () =>
        set((s) => ({
          relationshipAnimationEnabled: !s.relationshipAnimationEnabled,
          relAnimation:                 !s.relationshipAnimationEnabled,
        })),

      setRelationshipAnimation: (value) =>
        set({ relationshipAnimationEnabled: value, relAnimation: value }),

      // ── Line mode ──────────────────────────────────────────────────────────

      setDefaultLineMode: (mode) => set({ defaultLineMode: mode }),

      // ── Sidebar ────────────────────────────────────────────────────────────

      setSidebarHidden: (sidebarHidden) => set({ sidebarHidden }),

      toggleSidebar: () => set((s) => ({ sidebarHidden: !s.sidebarHidden })),

      toggleGroupCollapse: (groupId) =>
        set((s) => ({
          sbCollapsed: {
            ...s.sbCollapsed,
            [groupId]: !s.sbCollapsed[groupId],
          },
        })),

      // ── Defaults ───────────────────────────────────────────────────────────

      updateDefaults: (patch) =>
        set((s) => ({ defaults: { ...s.defaults, ...patch } })),

      setDefaults: (defaults) => set({ defaults }),

      resetDefaults: () => set({ defaults: INIT_DEFAULTS }),

      // ── Legacy aliases ─────────────────────────────────────────────────────

      setTheme: (theme) => set({ themePreference: theme, theme }),

      cycleTheme: () => {
        const { themePreference } = get()
        const idx  = THEME_CYCLE.indexOf(themePreference)
        const next = THEME_CYCLE[(idx + 1) % THEME_CYCLE.length]
        set({ themePreference: next, theme: next })
      },

      setLocale: (locale) => set({ language: locale, locale }),

      toggleRelAnimation: () =>
        set((s) => ({
          relationshipAnimationEnabled: !s.relationshipAnimationEnabled,
          relAnimation:                 !s.relationshipAnimationEnabled,
        })),
    }),
    {
      name: 'morpherd_settings',
      storage: settingsStorage,
      // resolvedTheme is intentionally excluded from persistence —
      // it is recalculated from themePreference + OS on each page load.
      partialize: (s) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { resolvedTheme, ...rest } = s
        return rest
      },
      onRehydrateStorage: () => (state) => {
        if (state?.language) void i18n.changeLanguage(state.language)
      },
    }
  )
)
